import React, { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Table, Button, Flex, Image, Tag, Switch, Typography, Tooltip } from "antd";
import { HolderOutlined } from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// import DebouncedInput from "../../Component/DebouncedInput";
import EditModal from "./EditModal";
import {
  getCategoryList,
  updateCategory,
  updateMultipleCategory,
} from "../../Services/category";
import { formatDate } from "../../utils/date";
import styled from "styled-components";
import {
  getCmsConfigurations,
  updateCmsConfigurationsBanner,
} from "../../Services/cms_configuration";

const StyledTable = styled(Table)`
  margin-top: 5px;
  ${({ maxHeight }) => `
    .ant-table {
      border-radius: 5px;
      font-size: 14px;
      white-space: nowrap;
      max-height: ${maxHeight || "calc(100vh - 140px)"};
      overflow: auto;
       border: 1px solid #c9c9c9;

    }
  `}
  .ant-table-thead > tr > th {
    position: sticky;
    top: 0;
    z-index: 4;
    padding: 10px;
    background-color: rgb(245, 243, 243);
    font-weight: 500;
  }
  .ant-table-tbody > tr > td {
    padding: 3px 16px;
    font-size: 13px;
    min-width: 100px;
  }
  .ant-table-pagination {
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    padding: 0 16px;
  }
  .ant-pagination-total-text {
    margin-right: auto;
  }
  .ant-pagination-options {
    margin-left: 8px;
  }
  .ant-table-footer {
    position: sticky;
    bottom: 0;
    z-index: 5;
    background-color: rgb(255, 255, 255);
    padding: 5px;
    /* border-top: 1px solid #a19aa8; */
  }
  .normal {
    min-width: 300px;
    white-space: normal;
    word-break: break-word;
  }
  .drag-handle-col {
    min-width: 40px !important; /* or whatever small width you prefer */
    padding: 0 !important;
    text-align: center;

    .anticon {
      font-size: 16px;
    }
  }
`;

const DragHandle = ({ id }) => {
  const { attributes, listeners } = useSortable({ id });

  return (
    <HolderOutlined
      {...attributes}
      {...listeners}
      style={{ cursor: "grab", color: "#999" }}
    />
  );
};

const SortableRow = (props) => {
  const { setNodeRef, transform, transition, isDragging } = useSortable({
    id: props["data-row-key"],
  });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging
      ? { zIndex: 9999, position: "relative", background: "#fafafa" }
      : {}),
  };

  return <tr {...props} ref={setNodeRef} style={style} />;
};

const columns = (showEditModal, deepLinkBaseUrl) => [
  {
    title: "",
    dataIndex: "sort",
    className: "drag-handle-col",
    render: (_, record) => <DragHandle id={record.id} />,
  },
  {
    title: "Category Name",
    dataIndex: "category_name",
  },
  {
    title: "Default Image",
    dataIndex: "full_category_image_url",
    render: (image) => (
      <Image
        src={image}
        width={40}
        height={40}
        preview={false}
        fallback="https://via.placeholder.com/50"
      />
    ),
  },
  {
    title: "Custom Image",
    dataIndex: "full_custom_image_url",
    render: (image) =>
      image ? (
        <Image
          src={image}
          width={30}
          height={30}
          preview={false}
          fallback="https://via.placeholder.com/50"
        />
      ) : (
        "-"
      ),
  },

  {
    title: "Use Custom Image",
    dataIndex: "use_custom_image",
    render: (useCustomImage) => (
      <Tag color={useCustomImage ? "green" : "red"}>
        {useCustomImage ? "Yes" : "No"}
      </Tag>
    ),
  },
  {
    title: "Default Order",
    dataIndex: "default_order",
  },
  {
    title: "Custom Order",
    dataIndex: "custom_order",
  },
  {
    title: "Active",
    dataIndex: "active",
    render: (active) => (
      <Tag color={active ? "green" : "red"}>{active ? "Yes" : "No"}</Tag>
    ),
  },
  {
    title: "Deep Link",
    dataIndex: "deep_link",
    key: "deep_link",
    render: (_, record) => {
      if (!deepLinkBaseUrl || !record) return "-";

      const generateOneLink = (baseUrl, params = {}) => {
        const queryString = Object.entries(params)
          .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(value)}`)
          .join('&');
        return `${baseUrl}${queryString}`;
      };

      // const fullLink = `${deepLinkBaseUrl}&c=Share Category&deep_link_value=category&deep_link_sub1=${record.category_slug}&deep_link_sub2=${record.category_name}&deep_link_sub3=${record.id}`;

      // Use the generic helper function
      const fullLink = generateOneLink(deepLinkBaseUrl, {
        c: "Share Category",
        deep_link_value: "category",
        deep_link_sub1: record.category_slug,
        deep_link_sub2: record.category_name,
        deep_link_sub3: record.id,
      });

      const shortLink =
        fullLink.length > 15 ? `${fullLink.slice(0, 15)}...` : fullLink;

      return (
        <Tooltip title={fullLink}>
          <Typography.Text
            copyable={{ text: fullLink }}
            style={{ cursor: "pointer" }}
          >
            {shortLink}
          </Typography.Text>
        </Tooltip>
      );
    },
  },
  {
    title: "Created At",
    dataIndex: "created_at",
    render: (date) => (date ? formatDate(date) : "-"),
  },
  {
    title: "Updated At",
    dataIndex: "updated_at",
    render: (date) => (date ? formatDate(date) : "-"),
  },
  {
    title: "Actions",
    key: "actions",
    fixed: "right",
    render: (_, record) => (
      <Flex gap="small">
        <Button type="link" onClick={() => showEditModal(record)}>
          Edit
        </Button>
      </Flex>
    ),
  },
];

function Category() {
  // const [searchTerm, setSearchTerm] = useState("");
  const [dataSource, setDataSource] = useState([]);
  // const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  const sensors = useSensors(useSensor(PointerSensor));

  const skip = (currentPage - 1) * pageSize;

  const {
    isLoading,
    refetch,
    data: res,
    isRefetching,
  } = useQuery({
    queryKey: ["category_list"],
    queryFn: () => getCategoryList({ skip, limit: pageSize }),
    refetchOnWindowFocus: false,
    retry: false,
  });

  useEffect(() => {
    if (res?.data) {
      setDataSource(res.data);
      // setTotalCount(res.total_count || 0);
    }
  }, [res]);

  const showEditModal = (record) => {
    setEditingRecord(record);
    setIsEditModalVisible(true);
  };

  const editMutation = useMutation({
    mutationFn: updateCategory,
    onSuccess: () => {
      refetch();
      setIsEditModalVisible(false);
    },
  });

  const handleEditSubmit = (id, values) => {
    editMutation.mutate({ id, ...values });
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (active.id !== over?.id) {
      const oldIndex = dataSource.findIndex((item) => item.id === active.id);
      const newIndex = dataSource.findIndex((item) => item.id === over.id);
      // const newData = arrayMove([...dataSource], oldIndex, newIndex);
      const newData = arrayMove(dataSource, oldIndex, newIndex).map(
        (item, index) => ({
          ...item,
          custom_order: index + 1,
        })
      );
      setDataSource(newData);

      // TODO: Optionally send reordered data to API here
    }
  };

  const [order, setOrder] = useState("default");

  const { data: categoryList, refetch: categoryRefetch } = useQuery({
    queryKey: ["cms_configuration_list"],
    queryFn: () => getCmsConfigurations(),
    keepPreviousData: true,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const deepLinkBaseUrl = categoryList?.deep_link_base_url || "";

  // Safely transform data.data into a config map
  const cmsConfigMap = useMemo(() => {
    if (!categoryList?.data) return {};

    return categoryList.data.reduce((acc, item) => {
      acc[item.name_slug] = item;
      return acc;
    }, {});
  }, [categoryList]);

  const category_order = useMemo(
    () => cmsConfigMap["category_order"] || {},
    [cmsConfigMap]
  );

  useEffect(() => {
    if (category_order?.use_custom) {
      setOrder("custom");
    } else {
      setOrder("default");
    }
  }, [category_order]);

  const editCategoryMutation = useMutation({
    mutationFn: updateCmsConfigurationsBanner,
    onSuccess: () => {
      categoryRefetch();
      refetch();
    },
  });

  const updateCategoryOrder = useMutation({
    mutationFn: updateMultipleCategory,
    onSuccess: () => {
      refetch();
    },
  });

  const handleCustomOrderChange = (val) => {
    setOrder(val);
    let payload = {
      id: category_order?.id,
      use_custom: val === "custom",
    };
    editCategoryMutation.mutate({ ...payload });
  };

  const handleSave = () => {
    const payloadData = dataSource.map((item, index) => ({
      id: item.id,
      custom_order: index + 1,
    }));
    let payload = {
      custom_orders: payloadData,
    };
    updateCategoryOrder.mutate({ ...payload });
  };

  return (
    <div>
      <Flex justify="space-between" align="center">
        <h3>Category Master List</h3>

        <Flex gap="small" align="center">
          <span> Use Custom Order :</span>
          <Switch
            checked={order === "custom"}
            onChange={(checked) =>
              handleCustomOrderChange(checked ? "custom" : "default")
            }
            checkedChildren={editCategoryMutation?.isPending ? "Loading..." : "Custom"}
            unCheckedChildren={editCategoryMutation?.isPending ? "Loading..." : "Default"}
            disabled={editCategoryMutation?.isPending}
          />
          <Button
            type="primary"
            onClick={handleSave}
            loading={updateCategoryOrder?.isPending}
          >
            Save Sort
          </Button>
        </Flex>
      </Flex>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext
          items={dataSource.map((item) => item.id)}
          strategy={verticalListSortingStrategy}
        >
          <StyledTable
            columns={columns(showEditModal, deepLinkBaseUrl)}
            dataSource={dataSource}
            loading={isLoading || isRefetching || editCategoryMutation?.isPending }
            rowKey="id"
            // pagination={{
            //   current: currentPage,
            //   pageSize: pageSize,
            //   total: totalCount,
            //   showSizeChanger: true,
            //   showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            //   pageSizeOptions: ["20", "50", "100"],
            // }}
            pagination={false}
            components={{
              body: {
                row: SortableRow,
              },
            }}
            onChange={handleTableChange}
            locale={{ emptyText: isLoading ? "Loading" : "No Data" }}
            // footer={tableFooter}
          />
        </SortableContext>
      </DndContext>

      <EditModal
        isModalVisible={isEditModalVisible}
        handleOk={handleEditSubmit}
        handleCancel={() => setIsEditModalVisible(false)}
        loading={editMutation.isPending}
        initialValues={editingRecord}
      />
    </div>
  );
}

export default Category;
